import {
  Clock,
  Users,
  MapPin,
  DollarSign,
  Calendar,
  Sun,
  Award,
  GraduationCap,
  MessageCircle,
  FileText,
  Target,
  CheckCircle2,
  Smartphone,
} from 'lucide-react';
import Year3FoundationsTemplate, {
  type Year3FoundationsPageData,
} from './templates/Year3FoundationsTemplate';

const PAGE: Year3FoundationsPageData = {
  route: '/programs/nsw/year-4-foundations-english-tutoring',
  breadcrumb: ['NSW programs', 'Foundations', 'Year 4 English'],
  pill: 'Year 4 · NSW curriculum',
  h1: 'Year 4 English tutoring in NSW',
  subtitle:
    'Strengthen reading, writing and grammar in small groups - aligned to the NSW NESA Stage 2 English syllabus and a strong springboard into Year 5.',
  quickFacts: [
    { icon: Clock, text: '{DURATION_SHORT}' },
    { icon: Users, text: '8-12 students' },
    { icon: MapPin, text: 'In-centre + online' },
    { icon: DollarSign, text: '{PRICE} / session' },
  ],
  courseDetailsHeading: 'Year 4 English',
  pricingHeading: 'Year 4 English',
  relatedHeading: 'More for Year 4 students',
  tabs: [
    {
      id: 'term',
      icon: Calendar,
      name: 'Term course',
      sub: '10 weekly sessions per term',
      items: [
        {
          label: 'Overview',
          name: 'Course overview',
          heading: 'Term Course overview',
          para:
            'The Year 4 English term course runs 10 sessions per term, {DURATION_LENGTH} each, in small groups of 8-12 students. Lessons are aligned to the NSW NESA Stage 2 English syllabus and consolidate the foundations needed for Year 5 and beyond.',
          bullets: [
            'Reading comprehension, writing and grammar covered across the term',
            'Content sequenced alongside the school year so tutoring and classroom work reinforce each other',
            'Strong preparation for the Year 4 OC opportunity class test (if sitting) and Year 5 NAPLAN',
          ],
        },
        {
          label: 'Audience',
          name: "Who it's for",
          heading: "Who it's for",
          para: 'This course is ideal for students who…',
          bullets: [
            'Want to get ahead of upcoming school content early',
            'Aim for stronger performance in class tasks and assessments',
            'Are considering the OC opportunity class test in Year 4',
            'Are building writing confidence through regular guided practice',
            '(Parents) want progress to be visible and reported each term',
          ],
        },
        {
          label: 'Benefits',
          name: "What you'll gain",
          heading: "What you'll gain",
          para:
            'By the end of the term your child will have made measurable progress in:',
          bullets: [
            'Reading comprehension - inferential reasoning, text structure, sophisticated vocabulary',
            'Writing - narrative, informative and persuasive texts with structured feedback on each',
            'Grammar and punctuation - complex sentences, paragraphing, editing skills',
            'OC readiness - thinking skills, reading and writing under timed conditions',
          ],
        },
        {
          label: 'Content',
          name: "What's covered",
          heading: "What's covered",
          para: 'Topics are sequenced across the four school terms:',
          bullets: [
            'Term 1 - Reading: inferential comprehension, vocabulary, text analysis',
            'Term 2 - Writing: narrative craft, character development, descriptive language',
            'Term 3 - Persuasive writing and OC/Year 5 NAPLAN preparation',
            'Term 4 - Language conventions: grammar, punctuation, spelling, extended writing',
          ],
        },
        {
          label: 'Program',
          name: 'How it works',
          heading: 'How it works',
          para:
            'Every session follows a consistent structure so students always know what to expect:',
          bullets: [
            'Free level assessment before the first session - no wasted lessons on work already mastered',
            'Each 60-min lesson: introduce the concept → model examples → guided practice → independent application',
            "Written and verbal feedback on every student's work each session",
            "End-of-term progress report so parents can see clearly what's improved",
          ],
        },
      ],
    },
    {
      id: 'holiday',
      icon: Sun,
      name: 'Holiday intensive',
      sub: '4 daily lessons in the holidays',
      items: [
        {
          label: 'Overview',
          name: 'Course overview',
          heading: 'Holiday intensive overview',
          para:
            'The Year 4 English holiday intensive runs 4 daily sessions over the school holidays, 60 minutes each. The program focuses on one writing genre intensively, allowing students to build skill and confidence quickly.',
          bullets: [
            '4 sessions, daily, 60 minutes each',
            'One writing genre - deep focus rather than broad coverage',
            'Same small groups and qualified educators as the term course',
            'No prior Schoolola enrolment needed',
          ],
        },
        {
          label: 'Audience',
          name: "Who it's for",
          heading: "Who it's for",
          para: 'The holiday intensive suits students who…',
          bullets: [
            'Are preparing for the OC test and want targeted writing practice',
            'Want a focused short boost before the next school term begins',
            "Can't commit to a full term but want structured, quality tuition",
            'Need to build confidence in one specific writing type quickly',
          ],
        },
        {
          label: 'Benefits',
          name: "What you'll gain",
          heading: "What you'll gain",
          para: 'After 4 sessions your child will have:',
          bullets: [
            'A solid understanding of one writing genre - structure, language features, planning approach',
            'Writing strategies for OC and NAPLAN under timed conditions',
            'Increased confidence to tackle writing tasks at school',
            'Specific written feedback on their own practice writing',
          ],
        },
        {
          label: 'Content',
          name: "What's covered",
          heading: "What's covered",
          para: 'The four sessions follow a structured daily sequence:',
          bullets: [
            'Day 1 - Genre overview: model texts, key features, planning framework',
            'Day 2 - Planning and drafting: scaffold practice, vocabulary development',
            'Day 3 - Revision and editing: sentence variety, language choices, checking against criteria',
            'Day 4 - Practice task under assessment conditions with full written feedback',
          ],
        },
        {
          label: 'Program',
          name: 'How it works',
          heading: 'How it works',
          para:
            'The intensive is designed for maximum impact across four consecutive sessions:',
          bullets: [
            'Register your interest - sessions fill quickly in school holiday periods',
            'Each day builds on the previous - daily attendance is expected',
            'Individual written feedback on Day 3 and Day 4 tasks included',
            'Students leave with a completed writing portfolio piece',
          ],
        },
      ],
    },
  ],
  inclusions: [
    {
      icon: Award,
      name: 'Qualified educators',
      desc: 'Teachers with NSW curriculum expertise and classroom experience - consistent across all NSW centres.',
    },
    {
      icon: Users,
      name: 'Small group classes',
      desc: '8-12 students per class. Your child gets seen, heard and responded to every lesson.',
    },
    {
      icon: GraduationCap,
      name: 'NSW curriculum-aligned',
      desc: 'Content mapped directly to NESA Stage 2 outcomes. Complements school, session by session.',
    },
    {
      icon: MessageCircle,
      name: 'Feedback every lesson',
      desc: "Structured written and verbal feedback on every student's work, every session.",
    },
    {
      icon: FileText,
      name: 'End-of-term report',
      desc: 'A clear parent-facing progress summary at end of each term - before school reports arrive.',
    },
    {
      icon: Target,
      name: 'Free assessment',
      desc: 'Level placement before the first session. No wasted lessons on work already mastered.',
    },
    {
      icon: CheckCircle2,
      name: 'OC & Year 5 NAPLAN ready',
      desc: 'Year 4 content sets the foundation for the OC test and Year 5 NAPLAN - built into the term sequence.',
    },
    {
      icon: Smartphone,
      name: 'Schoolola LMS',
      desc: 'Access theory lesson videos, online practice questions and track progress easily via the Schoolola LMS app.',
      images: [
        '/lovable-uploads/lms-parent-dashboard.png',
        '/lovable-uploads/lms-learning-dashboard.png',
        '/lovable-uploads/lms-diagnostic-overview.png',
        '/lovable-uploads/lms-quiz-question-1.png',
        '/lovable-uploads/lms-quiz-question-2.png',
        '/lovable-uploads/lms-results-report.png',
      ],
    },
  ],
  faq: [
    {
      q: 'Is {PRICE} the full price - are there any extras?',
      a: '{PRICE} covers the session. Term enrolments include the end-of-term progress report at no extra cost. {BOOKS}',
    },
    {
      q: 'What study materials are provided?',
      a: 'Study materials are provided as bound books - no loose papers. {BOOKS}',
    },
    {
      q: 'How is this different from what happens at school?',
      a: "Group sizes are smaller (8-12 students per class), lessons follow a consistent structured format, and every student receives written feedback on their own work each session. Content follows the same NESA Stage 2 outcomes - we're giving your child more practice and more attention within the same curriculum.",
    },
    {
      q: 'Is this the right course if my child is sitting the OC test?',
      a: 'This Foundations course strengthens the underlying English skills tested by OC - comprehension, vocabulary, and writing. For dedicated OC strategy, thinking skills and timed exam practice, pair this with our OC TMRW™ course.',
    },
    {
      q: 'What if my child misses a session?',
      a: 'We ask for consistent attendance as each session builds on the last. If a session is missed, your educator can note the key material to follow up at home - but there are no standard make-up classes. Contact your local centre.',
    },
    {
      q: 'Can a confident Year 4 student still be challenged?',
      a: 'Yes. The assessment places students within ability groups, not just year groups. A Year 4 student working above level will be placed in a group that challenges them accordingly.',
    },
  ],
  related: [
    {
      tag: 'Year 4 · NSW',
      name: 'Year 4 Maths',
      desc: 'Number, geometry and problem solving',
      href: '/programs/nsw/year-4-foundations-maths-tutoring',
    },
    {
      tag: 'Year 3-4 · NSW',
      name: 'OC TMRW™ Course',
      desc: 'Opportunity Class exam preparation',
      href: '/programs/oc-preparation',
    },
  ],
  seo: {
    title: 'Year 4 English Tutoring NSW - Small Groups | Schoolola',
    description:
      'Year 4 English tutoring in NSW. NESA Stage 2 aligned reading, writing, grammar with OC and Year 5 NAPLAN readiness. Small groups of 8-12 students..',
    schemaCourseName: 'Year 4 English Tutoring - NSW',
    schemaDescription:
      'Year 4 English tuition in NSW. Small groups of 8-12 students, aligned to NESA Stage 2. Reading, writing, grammar and OC/Year 5 NAPLAN readiness..',
  },
};

export default function NswYear4FoundationsEnglish() {
  return <Year3FoundationsTemplate page={PAGE} />;
}
